import type {
  Rental,
  RentalListParams,
  RentalListResponse,
  RentalWarning,
  RentalWarningResult,
} from "@/domain/rental";
import { generateEndpointWithSearchFilter } from "@/utils/utils";
import { apiClient, apiClientJSON } from "../apiClient";

export interface RentalListQueryParams {
  page?: number;
  search?: string;
  sort?: string;
  filter?: string;
  size?: number;
}

export const getRentals = (
  params: RentalListParams
): Promise<RentalListResponse> => {
  return apiClient
    .get("/journey-service/dashboard/journeys", { params })
    .then((r) => r.data);
};

export const getRentalsList = (
  params: RentalListQueryParams = {}
): Promise<RentalListResponse> => {
  const {
    page = 0,
    search = "",
    sort = "id,desc",
    filter = "",
    size = 10,
  } = params;
  const endpoint = generateEndpointWithSearchFilter(
    `/journey-service/dashboard/journeys?size=${size}`,
    page,
    search,
    sort,
    filter
  );
  return apiClientJSON.get(endpoint).then((r) => r.data);
};

export const getRentalById = (id: number): Promise<Rental> => {
  return apiClientJSON
    .get(`/journey-service/dashboard/journeys/${id}`)
    .then((r) => r.data);
};

export const updateRental = (
  id: number,
  dto: Partial<Rental>
): Promise<Rental> => {
  return apiClientJSON
    .put(`/journey-service/dashboard/journeys/${id}`, dto)
    .then((r) => r.data);
};

export interface FinishRentalDto {
  endTime: string;
  endStationCode?: string;
  endDockCode?: string;
  finalAmount?: number;
  shouldAdjustAmount?: boolean;
}

export const finishRental = (
  id: number,
  dto: FinishRentalDto
): Promise<void> => {
  return apiClientJSON
    .post(`/journey-service/dashboard/journeys/${id}/finish`, dto)
    .then(() => {});
};

export const getRentalStatuses = (): Promise<string[]> => {
  return apiClientJSON
    .get("/journey-service/dashboard/journeys/statuses")
    .then((r) => r.data);
};

export const getFinallyDataOfRental = (id: number): Promise<unknown> => {
  return apiClientJSON
    .get(`/journey-service/dashboard/journeys/${id}/state`)
    .then((r) => r.data);
};

export interface RentalPreviewCost {
  amount: number | null;
  currency: string | null;
  usedFreeMinutes: number | null;
}

export const getRentalPreviewCost = (
  id: number,
  endTime?: string
): Promise<RentalPreviewCost> => {
  return apiClientJSON
    .get(`/journey-service/dashboard/journeys/${id}/preview-cost`, {
      params: endTime ? { endTime } : undefined,
    })
    .then((r) => r.data);
};

export const retryPaymentForRental = (rentalId: number): Promise<void> => {
  return apiClientJSON
    .post(`/journey-service/dashboard/journeys/${rentalId}/pay`)
    .then(() => {});
};

export const sendWarningToRental = (
  rentalId: number,
  data: RentalWarning
): Promise<RentalWarningResult> => {
  return apiClient
    .post(`/journey-service/dashboard/journeys/${rentalId}/warning`, data)
    .then((r) => r.data);
};

export interface RentalUser {
  id: number;
  fullName: string;
  email?: string;
}

export const searchUsersForRental = (search: string): Promise<RentalUser[]> => {
  return apiClientJSON
    .get(
      `/payment-service/dashboard/users/list?search=${encodeURIComponent(search)}`
    )
    .then((r) => r.data || []);
};

export const simulateRental = (
  vehicleCode: number,
  cityId: number,
  stationCode?: number
): Promise<unknown> => {
  return apiClientJSON
    .post("/journey-service/dashboard/journeys/simulate", {
      vehicleCode,
      cityId,
      stationCode,
    })
    .then((r) => r.data);
};

/**
 * Directory data returned from the backend for constructing Firebase image paths.
 */
export interface RentalImageDirectoryData {
  id: number;
  cityCode: string;
  pricingPlanType: string;
}

/**
 * Fetches the directory data needed to construct the Firebase storage path for rental images.
 */
export const getRentalImageDirectoryData = (
  rentalId: number
): Promise<RentalImageDirectoryData> => {
  return apiClientJSON
    .get(`/journey-service/dashboard/journeys/${rentalId}/directory-data`)
    .then((r) => r.data);
};
